import {
  ALL_PERMISSIONS,
  isAdminOnly,
  PERMISSIONS,
  type Permission,
  type ProfileSummary,
} from "@pax/api-contract/common";
import { MAX_STAFF_PAY_CREDITS, type StaffRole } from "@pax/api-contract/staff/v1alpha1";
import { focusHaloFlush, panelMotion, Skeleton } from "@pax/ui";
import { Check, ChevronRight, Lock } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";
import { AccountAvatar } from "@/features/auth/components/AccountAvatar";
import { Failure, field, fieldLabel, ghostButton, Tabs } from "@/features/evals/components";
import { requestErrorMessage } from "@/lib/api";
import { type PayDraft, payFromDraft, payLabel, withinPayCap } from "./pay";

const listRow = `-mx-4 flex w-[calc(100%+2rem)] items-center gap-3 rounded-2xl px-4 py-3 text-left ${focusHaloFlush} ${panelMotion}`;

/** A row inside a `RowList`, which lights up under the pointer. */
export const listedRow = `flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left hover:bg-foreground/5 ${focusHaloFlush} ${panelMotion}`;

/** The quiet raised surface lists, figures, and notes sit on. */
export const surface = "rounded-2xl bg-foreground/[0.03] ring-1 ring-foreground/[0.06]";

/** Rows of people or roles gathered on one quiet surface. */
export function RowList({ children }: { children: ReactNode }) {
  return <div className={`${surface} p-1`}>{children}</div>;
}

/** What a list shows while it has nothing in it. */
export function EmptyList({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className={`${surface} px-6 py-10 text-center`}>
      <p className="text-sm font-medium">{title}</p>
      {children && (
        <p className="mx-auto mt-1 max-w-sm text-sm leading-6 text-muted-foreground">{children}</p>
      )}
    </div>
  );
}

/** A section's name, with an optional line beneath it and an action on its right. */
export function SectionHeading({
  title,
  hint,
  action,
}: {
  title: string;
  hint?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="mb-2.5 flex min-h-8 items-end justify-between gap-3">
      <div className="min-w-0">
        <h2 className="text-sm font-medium">{title}</h2>
        {hint && <p className="mt-0.5 text-xs leading-5 text-muted-foreground">{hint}</p>}
      </div>
      {action}
    </div>
  );
}

/** The quiet button a `SectionHeading` puts on its right. */
export const sectionAction = `${ghostButton} -mr-3 inline-flex h-8 shrink-0 items-center gap-1.5 px-3`;

/** A few figures side by side on one surface, each with its label above and a note beneath. */
export function StatGrid({
  stats,
  children,
}: {
  stats: { label: string; value: ReactNode; note?: ReactNode }[];
  /** Drawn under the figures, across the whole surface. */
  children?: ReactNode;
}) {
  return (
    <div className={surface}>
      <dl
        className="grid grid-cols-2 sm:grid-cols-(--stat-columns)"
        style={{ "--stat-columns": `repeat(${stats.length}, minmax(0, 1fr))` } as CSSProperties}
      >
        {stats.map(stat => (
          <div
            key={stat.label}
            className="min-w-0 border-foreground/[0.06] px-3 py-3.5 max-sm:even:border-l max-sm:[&:nth-child(n+3)]:border-t sm:px-4 sm:[&:not(:first-child)]:border-l"
          >
            <dt className="truncate text-xs text-muted-foreground">{stat.label}</dt>
            <dd className="mt-1 truncate text-lg font-semibold tracking-tight tabular-nums">
              {stat.value}
            </dd>
            {stat.note && (
              <dd className="mt-0.5 text-xs leading-5 text-muted-foreground">{stat.note}</dd>
            )}
          </div>
        ))}
      </dl>
      {children && <div className="border-t border-foreground/[0.06] px-4 py-4">{children}</div>}
    </div>
  );
}

export function AdminOnlyBadge() {
  return (
    <span className="shrink-0 rounded-full bg-foreground/[0.07] px-2 py-0.5 text-xs whitespace-nowrap text-foreground/70">
      Admin only
    </span>
  );
}

/** A role's description without its closing full stop, or its permissions when it has none. */
export const roleSummary = (role: StaffRole) =>
  role.description?.replace(/\.$/, "") ??
  (permissionNames(role.permissions).join(", ") || "No permissions");

/** A role in a `RowList`: its summary, its default pay, and the faces of the first people holding it. */
export function RoleRow({
  role,
  holders,
  locked,
  onClick,
}: {
  role: StaffRole;
  holders: readonly ProfileSummary[];
  locked: boolean;
  onClick: () => void;
}) {
  return (
    <button type="button" onClick={onClick} className={listedRow}>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span className="truncate text-sm font-medium">{role.name}</span>
          {locked && (
            <>
              <Lock aria-hidden className="size-3 shrink-0 text-muted-foreground" />
              <span className="sr-only">Only an admin can change this role.</span>
            </>
          )}
        </span>
        <span className="mt-0.5 block truncate text-xs leading-5 text-muted-foreground">
          {[roleSummary(role), role.defaultPay && `${payLabel(role.defaultPay)} by default`]
            .filter(Boolean)
            .join(" · ")}
        </span>
      </span>
      {!role.archivedAt && (
        <span className="flex shrink-0 items-center gap-2">
          <span className="flex gap-1 max-sm:hidden">
            {holders.slice(0, 3).map(person => (
              <PersonFace key={person.profileId} person={person} size={24} />
            ))}
          </span>
          <span className="text-xs text-muted-foreground tabular-nums">
            {role.activeAssignmentCount ? peopleCount(role.activeAssignmentCount) : "Nobody"}
          </span>
        </span>
      )}
      <OpensPage />
    </button>
  );
}

/** One quiet line on a list's surface, for a short list inside a form that has no rows yet. */
export function ListNote({ children }: { children: ReactNode }) {
  return (
    <RowList>
      <p className="px-3 py-2.5 text-sm text-muted-foreground">{children}</p>
    </RowList>
  );
}

/** The shape of a list of people while it loads. */
export function LoadingRows({ count = 4 }: { count?: number }) {
  return (
    <RowList>
      {Array.from({ length: count }, (_, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: placeholders have nothing else to key by
        <div key={index} className="flex items-center gap-3 px-3 py-2.5">
          <Skeleton className="size-9 shrink-0 rounded-full" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-3.5 w-36" />
            <Skeleton className="h-3 w-24" />
          </div>
        </div>
      ))}
    </RowList>
  );
}

/** The arrow at the end of a row that opens a page. */
export function OpensPage() {
  return <ChevronRight aria-hidden className="size-4 shrink-0 text-muted-foreground/60" />;
}

/** Role names as small chips. */
export function RoleChips({ names }: { names: readonly string[] }) {
  return (
    <span className="flex flex-wrap gap-1">
      {names.map(name => (
        <span
          key={name}
          className="rounded-full bg-foreground/[0.07] px-2 py-0.5 text-xs leading-4 whitespace-nowrap text-foreground/80"
        >
          {name}
        </span>
      ))}
    </span>
  );
}

export function PersonFace({
  person,
  size = 36,
  className,
}: {
  person: ProfileSummary;
  size?: 20 | 24 | 36 | 48 | 88;
  className?: string;
}) {
  return (
    <AccountAvatar
      name={person.displayName}
      image={person.avatar}
      size={size}
      className={className}
      fallbackClassName={size === 20 ? "text-[9px]" : size === 24 ? "text-[10px]" : undefined}
    />
  );
}

/** A person in a `RowList`: their picture, name, and username, with `detail` beneath. */
export function PersonRow({
  person,
  detail,
  trailing,
  onClick,
}: {
  person: ProfileSummary;
  detail?: ReactNode;
  trailing?: ReactNode;
  onClick: () => void;
}) {
  return (
    <button type="button" onClick={onClick} className={listedRow}>
      <PersonFace person={person} />
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-2">
          <span className="truncate text-sm font-medium">{person.displayName}</span>
          {person.username && (
            <span className="truncate text-xs text-muted-foreground">@{person.username}</span>
          )}
        </span>
        {detail && (
          <span className="mt-1 block truncate text-xs leading-5 text-muted-foreground">
            {detail}
          </span>
        )}
      </span>
      {trailing}
    </button>
  );
}

/** A failed request, in the words the API wrote for the person who made it. */
export function Problem({ error }: { error: unknown }) {
  return <Failure error={error ? requestErrorMessage(error) : null} />;
}

/**
 * Unpaid, or an amount of Pax credits paid once a month. `hint` replaces the line beneath the
 * amount, which still warns when the amount is over the pay cap. `label` is read aloud but not drawn
 * when a heading above already says it.
 */
export function PayFields({
  label = "Pay",
  labelShown = true,
  value,
  onChange,
  hint,
  disabled = false,
}: {
  label?: string;
  labelShown?: boolean;
  value: PayDraft;
  onChange: (value: PayDraft) => void;
  hint?: string;
  disabled?: boolean;
}) {
  const typed = payFromDraft(value);
  const tooMuch = !!typed && !withinPayCap(typed);
  return (
    <fieldset disabled={disabled} className="space-y-3 disabled:opacity-60">
      <legend className={labelShown ? `${fieldLabel} mb-1.5` : "sr-only"}>{label}</legend>
      <Tabs
        value={value === null ? "unpaid" : "paid"}
        options={[
          { value: "unpaid", label: "Unpaid" },
          { value: "paid", label: "Paid in Pax credits" },
        ]}
        onChange={next => onChange(next === "paid" ? (value ?? "") : null)}
      />
      {value !== null && (
        <>
          <label className="relative block w-52">
            <input
              aria-label="Credits a month"
              inputMode="decimal"
              placeholder="0"
              value={value}
              onChange={event => onChange(event.target.value)}
              className={`${field} pr-28 tabular-nums`}
            />
            <span className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-sm text-muted-foreground">
              credits a month
            </span>
          </label>
          <p className="text-xs leading-5 text-muted-foreground">
            {hint}
            {hint && tooMuch && " "}
            {(tooMuch || !hint) && (
              <span className={tooMuch ? "text-destructive" : undefined}>
                Up to {MAX_STAFF_PAY_CREDITS} credits a month.
              </span>
            )}
          </p>
        </>
      )}
    </fieldset>
  );
}

/** The box drawn for a visually hidden checkbox placed just before it with the `peer` class. */
export function CheckMark({ checked }: { checked: boolean }) {
  return (
    <span
      aria-hidden
      className={`mt-0.5 grid size-4.5 shrink-0 place-items-center rounded-md peer-focus-visible:ring-2 peer-focus-visible:ring-foreground/40 ${panelMotion} ${checked ? "bg-foreground text-background" : "ring-1 ring-foreground/25 ring-inset"}`}
    >
      {checked && <Check className="size-3" strokeWidth={3} />}
    </span>
  );
}

/**
 * Each permission in `shown`, every one by default, as a checkbox. Admin-only ones are locked for
 * anyone who is not an admin.
 */
export function PermissionChecklist({
  value,
  onChange,
  isAdmin,
  disabled = false,
  shown = ALL_PERMISSIONS,
}: {
  value: readonly Permission[];
  onChange: (value: Permission[]) => void;
  isAdmin: boolean;
  disabled?: boolean;
  shown?: readonly Permission[];
}) {
  return (
    <fieldset disabled={disabled}>
      <legend className="sr-only">Permissions</legend>
      {shown.map(permission => {
        const checked = value.includes(permission);
        const adminOnly = isAdminOnly(permission);
        const locked = disabled || (adminOnly && !isAdmin);
        return (
          <label
            key={permission}
            className={`${listRow} items-start has-focus-visible:bg-foreground/5 ${locked ? "" : "cursor-pointer hover:bg-foreground/5"}`}
          >
            <input
              type="checkbox"
              checked={checked}
              disabled={locked}
              onChange={() =>
                onChange(
                  checked
                    ? value.filter(held => held !== permission)
                    : ALL_PERMISSIONS.filter(
                        known => known === permission || value.includes(known),
                      ),
                )
              }
              className="peer sr-only"
            />
            <CheckMark checked={checked} />
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium">{PERMISSIONS[permission].name}</span>
              <span className="mt-0.5 block text-xs leading-5 text-muted-foreground">
                {PERMISSIONS[permission].description}
              </span>
            </span>
            {adminOnly && <AdminOnlyBadge />}
          </label>
        );
      })}
    </fieldset>
  );
}

export const permissionNames = (permissions: readonly Permission[]) =>
  permissions.map(permission => PERMISSIONS[permission].name);

/** The permissions whose name or description holds what was typed, in their usual order. */
export function permissionsMatching(typed: string): Permission[] {
  const search = typed.trim().toLowerCase();
  return ALL_PERMISSIONS.filter(permission =>
    `${PERMISSIONS[permission].name} ${PERMISSIONS[permission].description}`
      .toLowerCase()
      .includes(search),
  );
}

export const peopleCount = (count: number) => (count === 1 ? "1 person" : `${count} people`);

export const roleCount = (count: number) => (count === 1 ? "1 role" : `${count} roles`);
